import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth, apiErr } from "@/context/AuthContext";

export default function Login({ admin = false }) {
  const { login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: admin ? "" : "", password: "", remember: true, totp: "" });
  const [need2fa, setNeed2fa] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(form);
      if (res.require_2fa) {
        setNeed2fa(true);
        toast.info("Lütfen 2FA kodunuzu girin.");
        return;
      }
      toast.success("Giriş başarılı.");
      nav(res.user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-primary p-12 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="relative"><Logo variant="white" /></div>
        <div className="relative">
          <h2 className="text-3xl font-extrabold tracking-tighter text-white leading-tight">
            Kripto ödemelerinizi<br />tek panelden yönetin.
          </h2>
          <p className="mt-4 text-white/70 max-w-sm leading-relaxed">
            Coinbuy ile ödemelerinizi takip edin, bakiyenizi görün ve güvenle para çekin.
          </p>
        </div>
        <div className="relative text-xs text-white/50">© {new Date().getFullYear()} Coinbuy.org</div>
      </div>

      <div className="flex flex-col p-6 sm:p-12">
        <div className="flex items-center justify-between">
          <Link to="/" className="lg:hidden"><Logo /></Link>
          <Link to="/" className="hidden lg:inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft size={15} /> Ana Sayfa
          </Link>
          <ThemeToggle />
        </div>

        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-sm">
            <h1 className="text-2xl font-extrabold tracking-tighter">{admin ? "Yönetici Girişi" : "Tekrar Hoş Geldiniz"}</h1>
            <p className="mt-2 text-sm text-muted-foreground">Hesabınıza giriş yapın.</p>

            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <Label className="text-sm">E-posta</Label>
                <Input required type="email" value={form.email} onChange={set("email")} data-testid="login-email" className="mt-1.5" placeholder="ornek@eposta.com" />
              </div>
              <div>
                <Label className="text-sm">Şifre</Label>
                <Input required type="password" value={form.password} onChange={set("password")} data-testid="login-password" className="mt-1.5" placeholder="••••••••" />
              </div>
              {need2fa && (
                <div>
                  <Label className="text-sm">2FA Kodu</Label>
                  <Input value={form.totp} onChange={set("totp")} data-testid="login-totp" className="mt-1.5 tracking-[0.3em] text-center" placeholder="000000" maxLength={6} />
                </div>
              )}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                  <Checkbox checked={form.remember} onCheckedChange={(v) => setForm((f) => ({ ...f, remember: !!v }))} data-testid="login-remember" />
                  Beni Hatırla
                </label>
                <span className="text-sm text-muted-foreground hover:text-primary cursor-pointer">Şifremi Unuttum</span>
              </div>
              <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-full h-11">
                {loading ? <Loader2 className="animate-spin" size={17} /> : "Giriş Yap"}
              </Button>
            </form>

            {!admin && (
              <p className="mt-6 text-center text-sm text-muted-foreground">
                Hesabınız yok mu?{" "}
                <Link to="/register" className="font-semibold text-primary hover:underline">Kayıt Olun</Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
